import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { buildProfileEvent, parseProfileContent } from '../utils/profile.js'
import { LS_PROFILE_PREFIX, PROFILE_CAP_BYTES } from '../utils/constants.js'

// Fetch a Nostr kind:0 profile for the given pubkey. Hydrates from
// localStorage for instant render, then revalidates in the background
// against the relay pool. Keeps newest by created_at across relays so
// stale copies never overwrite fresh ones.
//
// Returns:
//   profile  — null | { name, displayName, picture, nip05, about }
//   loading  — true only when we have no cached profile AND haven't
//              heard back from the relays yet.
//
// When pubkey is null, returns { profile: null, loading: false }
// (the caller isn't signed in; nothing to fetch).
export function useProfile({ subscribe, pubkey, sign, publish }) {
  const [profile, setProfile] = useState(null)
  // Raw parsed kind:0 content. Held alongside the normalized profile so
  // updateProfile can preserve unknown custom-client fields on save.
  const [rawContent, setRawContent] = useState(null)
  const [loading, setLoading] = useState(false)

  // Reset state when pubkey changes; serve from cache instantly when
  // available so the avatar doesn't flash on every navigation.
  useEffect(() => {
    if (!pubkey) {
      setProfile(null)
      setRawContent(null)
      setLoading(false)
      return
    }
    const cached = loadCachedProfile(pubkey)
    if (cached) {
      setProfile(cached.profile)
      setRawContent(cached.rawContent || null)
      setLoading(false)
    } else {
      setProfile(null)
      setRawContent(null)
      setLoading(true)
    }
  }, [pubkey])

  // Live kind:0 subscription. The wrapper in useNostr.js accepts an
  // array of filters and unwraps to a single object before SimplePool
  // sees it (CLAUDE.md "SimplePool filter gotcha").
  useEffect(() => {
    if (!subscribe || !pubkey) return
    let newestCreatedAt = loadCachedProfile(pubkey)?.created_at || 0

    const sub = subscribe(
      [{ kinds: [0], authors: [pubkey], limit: 1 }],
      (event) => {
        if (!event || event.kind !== 0) return
        if (event.created_at <= newestCreatedAt) return
        newestCreatedAt = event.created_at
        const parsed = parseProfileContent(event.content)
        if (!parsed) return
        let raw = null
        try { raw = JSON.parse(event.content) } catch { /* keep null */ }
        setProfile(parsed)
        setRawContent(raw)
        setLoading(false)
        saveCachedProfile(pubkey, {
          profile: parsed,
          rawContent: raw,
          created_at: event.created_at,
        })
      },
      () => setLoading(false),
    )
    // Safety timeout: stop loading if no EOSE arrives within 6s.
    const t = setTimeout(() => setLoading(false), 6000)
    return () => {
      clearTimeout(t)
      try { sub?.close?.() } catch { /* noop */ }
    }
  }, [subscribe, pubkey])

  // Sign and publish an updated kind:0 event. Updates local state and
  // cache only after publish reports at least one accepting relay
  // (useNostr.publish throws when none accept). On sign or publish
  // failure, no local mutation; the caller catches and surfaces.
  const updateProfile = useCallback(async (values) => {
    if (!pubkey) throw new Error('Not signed in.')
    if (!sign || !publish) throw new Error('Profile editing is not wired up here.')

    try {
      const template = buildProfileEvent(values, rawContent || {}, pubkey)
      const signed = await sign(template)
      await publish(signed)

      // Re-parse from the signed content so what we cache matches what
      // we just published byte-for-byte.
      const nextProfile = parseProfileContent(signed.content)
      let nextRaw = null
      try { nextRaw = JSON.parse(signed.content) } catch { /* keep null */ }
      if (nextProfile) {
        setProfile(nextProfile)
        setRawContent(nextRaw)
        saveCachedProfile(pubkey, {
          profile: nextProfile,
          rawContent: nextRaw,
          created_at: signed.created_at,
        })
      }
      return signed
    } catch (err) {
      // Attach field NAMES the user tried to set so we can spot which
      // edits fail most often. Never the values — about-field content
      // can be personal even though it's technically public on relays.
      const editedFields = Object.keys(values || {}).filter(
        (k) => typeof values[k] === 'string' && values[k].trim(),
      )
      Sentry.withScope((s) => {
        s.setTag('op', 'profile_publish')
        s.setExtra('editedFields', editedFields)
        Sentry.captureException(err)
      })
      throw err
    }
  }, [pubkey, sign, publish, rawContent])

  return { profile, loading, updateProfile }
}

function loadCachedProfile(pubkey) {
  try {
    const raw = localStorage.getItem(LS_PROFILE_PREFIX + pubkey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.profile) return null
    return parsed
  } catch { return null }
}

function saveCachedProfile(pubkey, payload) {
  try {
    const json = JSON.stringify(payload)
    if (json.length > PROFILE_CAP_BYTES) return
    localStorage.setItem(LS_PROFILE_PREFIX + pubkey, json)
  } catch { /* quota — silently drop */ }
}
